// import { adjustOpacity } from "@/utils/ColorUtils";

import { CASCADABLE_PROPERTIES, FEATURE_TYPES } from "@/models/Feature.model";
import { adjustOpacity } from "@/utils/ColorUtils";
import { getPointCategory } from "@/utils/constants";
import isEqual from "lodash/isEqual";

const isLineFeature = (type) =>
  type === FEATURE_TYPES.LineString || type === FEATURE_TYPES.MultiLineString;
const isPointFeature = (type) => type === FEATURE_TYPES.Point || type === "MultiPoint";

const matchesFilters = (feature, filters) => {
  const type = feature?.geometry?.type;
  const showLines = filters?.showLines ?? true;
  const showPoints = filters?.showPoints ?? true;
  if (isLineFeature(type) && !showLines) return false;
  if (isPointFeature(type) && !showPoints) return false;

  if (isLineFeature(type)) {
    const objectIdFilter = `${filters?.objectId || ""}`.trim();
    if (objectIdFilter) {
      const objectIdValue =
        feature?.properties?.OBJECTID ??
        feature?.properties?.OBJECT_ID ??
        feature?.properties?.object_id ??
        feature?.properties?.ObjectId ??
        feature?.properties?.objectid;
      if (
        objectIdValue === undefined ||
        `${objectIdValue}` !== objectIdFilter
      ) {
        return false;
      }
    }

    const splitCategoryFilter = filters?.splitCategory ?? "all";
    if (splitCategoryFilter !== "all") {
      const splitCategory =
        feature?.properties?.SPLIT_CATEGORY ??
        feature?.properties?.split_category ??
        feature?.properties?.SplitCategory;
      if (splitCategory && splitCategory !== splitCategoryFilter) return false;
    }

    const capacityFilter = filters?.capacity ?? "all";
    if (capacityFilter !== "all") {
      const capacity = Number(feature?.properties?.CAPACITYKV);
      if (!Number.isFinite(capacity) || capacity !== Number(capacityFilter)) {
        return false;
      }
    }

    const classFilter = filters?.lineClass ?? "all";
    if (classFilter !== "all") {
      const featureClass = feature?.properties?.CLASS ?? feature?.properties?.class ?? feature?.properties?.Class;
      if (featureClass !== classFilter) return false;
    }
  }

  if (isPointFeature(type)) {
    const pointTypeFilter = filters?.pointType ?? "all";
    if (pointTypeFilter !== "all") {
      if (getPointCategory(feature) !== pointTypeFilter) return false;
    }

    if (getPointCategory(feature) === "bus") {
      const busNumberFilter = `${filters?.busNumber || ""}`.trim().toLowerCase();
      if (busNumberFilter) {
        const busNumber = `${feature?.properties?.["6_digit_bus_number"] ?? ""}`.toLowerCase();
        if (!busNumber.includes(busNumberFilter)) return false;
      }
      const busStationFilter = `${filters?.busStation || ""}`.trim().toLowerCase();
      if (busStationFilter) {
        const busStation = `${feature?.properties?.station ?? ""}`.toLowerCase();
        if (!busStation.includes(busStationFilter)) return false;
      }
      const busVRatedFilter = `${filters?.busVRated || ""}`.trim().toLowerCase();
      if (busVRatedFilter) {
        const busVRated = `${feature?.properties?.v_rated ?? ""}`.toLowerCase();
        if (!busVRated.includes(busVRatedFilter)) return false;
      }
      const busRegionFilter = `${filters?.busRegion || ""}`.trim().toLowerCase();
      if (busRegionFilter) {
        const busRegion = `${feature?.properties?.region ?? ""}`.toLowerCase();
        if (!busRegion.includes(busRegionFilter)) return false;
      }
    }
  }

  const stateFilter = filters?.state ?? "all";
  if (stateFilter !== "all") {
    const stateValue = isPointFeature(type)
      ? feature?.properties?.STATE ??
        feature?.properties?.state ??
        feature?.properties?.State
      : feature?.properties?.STATE;
    if (stateValue !== stateFilter) return false;
  }

  const operationalFilter = filters?.operationalStatus ?? "all";
  if (operationalFilter !== "all") {
    const operationalValue = isPointFeature(type)
      ? feature?.properties?.OPERATIONALSTATUS ??
        feature?.properties?.operationalStatus ??
        feature?.properties?.OperationalStatus
      : feature?.properties?.OPERATIONALSTATUS;
    if (operationalValue !== operationalFilter) return false;
  }

  const query = `${filters?.searchQuery || ""}`.trim().toLowerCase();
  if (!query) return true;
  const tokens = query
    .split(";")
    .map((token) => token.trim())
    .filter(Boolean);
  if (!tokens.length) return true;
  const name = `${feature?.properties?.name || ""}`.toLowerCase();
  const id = `${feature?.id || feature?.properties?.id || ""}`.toLowerCase();
  const typeName = `${feature?.geometry?.type || ""}`.toLowerCase();
  return tokens.some(
    (token) =>
      name.includes(token) || id.includes(token) || typeName.includes(token)
  );
};

export default {
  getFilename: (state) => {
    return state.filename;
  },

  getFeatureFilters: (state) => {
    return state.featureFilters;
  },

  getFilteredFeatureIds: (state) => {
    const pinned = new Set(state.pinnedFeatureIds || []);
    return [...state.featureIds].filter((featureId) =>
      pinned.has(featureId) ||
      matchesFilters(state.features[featureId], state.featureFilters)
    );
  },

  getGeoJson: (state) => {
    const shouldFadeUnselected = state.selectedFeatureIds.length > 0;
    const pinned = new Set(state.pinnedFeatureIds || []);
    return {
      type: "FeatureCollection",
      features: [...state.featureIds]
        .filter((featureId) =>
          pinned.has(featureId) ||
          matchesFilters(state.features[featureId], state.featureFilters)
        )
        .filter((featureId) => {
          if (
            state.features[featureId].properties.isHidden ||
            (state.activeEditMode &&
              state.selectedFeatureIds.indexOf(featureId) > -1)
          ) {
            return false;
          } else {
            return true;
          }
        })
        .map((item) => {
          const isSelected = state.selectedFeatureIds.indexOf(item) > -1;
          const properties = state.features[item].properties;
          return {
            ...state.features[item],
            properties: {
              ...properties,
              ...(shouldFadeUnselected && !isSelected && properties.fillColor
                ? {
                    fillColor: adjustOpacity(
                      state.features[item].properties.fillColor,
                      0.2
                    ),
                  }
                : {}),
              ...(shouldFadeUnselected && !isSelected && properties.lineColor
                ? {
                    lineColor: adjustOpacity(
                      state.features[item].properties.lineColor,
                      0.2
                    ),
                  }
                : {}),
            },
          };
        }),
    };
  },

  getFilteredFeatures: (state) => {
    const pinned = new Set(state.pinnedFeatureIds || []);
    return [...state.featureIds]
      .filter((featureId) => {
        if (pinned.has(featureId)) return true;
        return matchesFilters(state.features[featureId], state.featureFilters);
      })
      .reverse()
      .map((item) => {
        const isSelected = state.selectedFeatureIds.indexOf(item) > -1;
        const isPinned = pinned.has(item);
        return {
          ...state.features[item],
          isSelected,
          isPinned,
        };
      });
  },

  getFeatures: (state) => {
    return [...state.featureIds].reverse().map((item) => {
      const isSelected = state.selectedFeatureIds.indexOf(item) > -1;
      return {
        ...state.features[item],
        isSelected,
      };
    });
  },

  getActiveEditMode: (state) => {
    return state.activeEditMode;
  },

  getActiveTool: (state) => {
    return state.activeTool;
  },

  getHoveredFeatureId: (state) => {
    return state.hoveredFeatureId;
  },

  getSelectedFeatureIds: (state) => {
    return state.selectedFeatureIds || [];
  },

  getGroupedPropertiesFromSelected: (state) => {
    const groupedProperties = {};
    state.selectedFeatureIds.forEach((featureId) => {
      const properties = state.features[featureId].properties;
      CASCADABLE_PROPERTIES.forEach((key) => {
        if (groupedProperties[key] === undefined) {
          groupedProperties[key] = [
            {
              featureIds: [featureId],
              value: properties[key],
            },
          ];
        } else {
          const group = groupedProperties[key].find((item) =>
            isEqual(item.value, properties[key])
          );
          if (group) {
            group.featureIds.push(featureId);
          } else {
            groupedProperties[key].push({
              featureIds: [featureId],
              value: properties[key],
            });
          }
        }
      });
    });
    return groupedProperties;
  },

  getSelectedFeatures: (state) => {
    return (
      state.selectedFeatureIds &&
      state.selectedFeatureIds.map((id) => state.features[id])
    );
  },

  getFocusedFeature: (state) => {
    return state.focusedFeatureId && state.features[state.focusedFeatureId];
  },

  isGeneratingImgage: (state) => {
    return state.isGeneratingImage;
  },

  getBoundingBox: (state) => {
    return state.boundingBox;
  },
  getPinnedFeatureIds: (state) => {
    return state.pinnedFeatureIds || [];
  },
  getHighlightedFeatureIds: (state) => {
    return state.highlightedFeatureIds || [];
  },
  getCanUndo: (state) => {
    return (state.undoStack || []).length > 0;
  },
  getCanRedo: (state) => {
    return (state.redoStack || []).length > 0;
  },
  getIsImportLocked: (state) => {
    return !!state.importLocked;
  },
};
